/**
 * useSyntheticData.ts — Hook for nself-synthetic-monitor plugin (port 3836)
 *
 * Purpose:    Fetch and manage synthetic check definitions and results.
 * Inputs:     None (reads from plugin backend port 3836)
 * Outputs:    { state, data, error, refresh, saveCheck, deleteCheck }
 * Constraints: Client-side only; uses pluginFetch; AsyncState for UI
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useCallback, useEffect, useState } from 'react'
import { pluginFetch } from '../lib/plugin-client'
import type { SentryError } from '../lib/sentry-error'
import type { AsyncState } from '../components/AsyncScreen'

const PORT = 3836

export type CheckScriptType = 'http' | 'browser'
export type GeoRegion = 'us-east' | 'us-west' | 'eu-west' | 'ap-southeast' | 'ap-northeast'

export interface SyntheticCheck {
  id: string
  name: string
  url: string
  scriptType: CheckScriptType
  geoRegions: GeoRegion[]
  intervalMinutes: number
  enabled: boolean
  createdAt: string
}

export interface CheckResult {
  id: string
  checkId: string
  region: GeoRegion
  passed: boolean
  latencyMs: number
  errorMessage?: string
  checkedAt: string
}

export interface RegressionEntry {
  checkId: string
  checkName: string
  region: GeoRegion
  /** ISO timestamp of regression start */
  detectedAt: string
  /** 'recovered' | 'ongoing' */
  status: 'recovered' | 'ongoing'
}

export interface SyntheticData {
  checks: SyntheticCheck[]
  recentResults: CheckResult[]
  regressions: RegressionEntry[]
}

export function useSyntheticData() {
  const [state, setState] = useState<AsyncState>('loading')
  const [data, setData] = useState<SyntheticData | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  const load = useCallback(async () => {
    setState('loading')
    setError(undefined)
    if (!navigator.onLine) { setState('offline'); return }
    const result = await pluginFetch<SyntheticData>(PORT, '/api/synthetic/summary')
    if (result.ok) {
      setData(result.value)
      setState(result.value.checks.length === 0 ? 'empty' : 'success')
    } else {
      const e = result.error
      if (e.type === 'bundle_required') setState('permission-denied')
      else if (e.type === 'rate_limit') setState('rate-limited')
      else if (e.type === 'plugin_offline') setState('offline')
      else setState('error')
      setError(e)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const saveCheck = useCallback(
    async (check: Omit<SyntheticCheck, 'id' | 'createdAt'> & { id?: string }): Promise<boolean> => {
      const isNew = !check.id
      const result = await pluginFetch(
        PORT,
        isNew ? '/api/synthetic/checks' : `/api/synthetic/checks/${check.id}`,
        { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(check) },
      )
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const deleteCheck = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await pluginFetch(PORT, `/api/synthetic/checks/${id}`, { method: 'DELETE' })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  return { state, data, error, refresh: load, saveCheck, deleteCheck }
}
